import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import { DocumentItemPartial, FavoriteFieldEntity } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { SemaphoreViewComponent } from '../../../../../components/semaphore-view/semaphore-view.component';
import {
    cleanSemaphoreText,
    DocumentSemaphoreValue,
    emptySemaphoreItem,
    parseSemaphoreValue,
    sameSemaphoreValue,
    SEMAPHORE_KEYS,
    SEMAPHORE_LIMITS,
    SEMAPHORE_SCALE_LABEL_KEYS,
    semaphoreCounts,
    SemaphoreStatus,
} from '../../../../../semaphore-options';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { CardTypes } from '../_card-base/enums/card-types.enum';
import { RaLookPanelDirective } from '../_card-base/look-panel.directive';
import { UndoToastService } from '../_card-base/undo-toast.service';
import { TableCellTextDirective } from '../table-card/table-cell-text.directive';


/**
 * Card do SEMÁFORO (item SEMAPHORE): a lista como imprime, editável no lugar. Cada item
 * tem o nome (texto de uma linha) e as três opções da escala (clique escolhe, clique de
 * novo limpa). O resumo sai sempre no topo. No hover, a alça e o remover. "+ Item" no pé.
 * Formato e palavras da escala moram no rail.
 *
 * Salva o valor INTEIRO (update-semaphore) ao sair de um campo alterado, ao avaliar, ao
 * adicionar, remover e reordenar. Enquanto um campo tem foco, o eco do servidor não
 * sobrescreve o que está sendo digitado.
 */
@Component({
    selector: 'semaphore-card',
    templateUrl: 'semaphore-card.component.html',
    styleUrl: 'semaphore-card.component.scss',
    imports: [
        CardBaseComponent,
        SemaphoreViewComponent,
        TableCellTextDirective,
        RaIconComponent,
        RaEditTooltipDirective,
        RaLookPanelDirective,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslatePipe,
    ],
})
export class SemaphoreCardComponent {
    public field = input<DocumentItemPartial>();
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);

    public readonly cardTypes = CardTypes;
    public readonly keys = SEMAPHORE_KEYS;
    public readonly limits = SEMAPHORE_LIMITS;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    private _editItemsService = inject(EditItemsService);
    private _translate = inject(TranslateService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _undoToast = inject(UndoToastService);

    /** Valor em edição (cópia local; o servidor recebe no commit). */
    public value = signal<DocumentSemaphoreValue>({ scale: [], items: [] });

    /** Algum campo tem foco: o eco do servidor espera. */
    private _focused = signal(false);

    public currentField = computed(() => this.getCurrentField());
    public canAdd = computed(() => this.value().items.length < SEMAPHORE_LIMITS.MAX_ITEMS);
    public stats = computed(() => semaphoreCounts(this.value().items));

    public blocked = computed(
        () =>
            this.editService.loading() ||
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
    );

    constructor() {
        // Servidor (ou realtime) mudou o valor: adota, a não ser que o usuário esteja
        // digitando agora.
        effect(() => {
            const serverValue = parseSemaphoreValue(this.currentField()?.value);
            if (untracked(this._focused)) return;
            untracked(() => {
                if (!sameSemaphoreValue(serverValue, this.value())) this.value.set(serverValue);
            });
        });
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }

    /** Palavra da escala: a do item ou, em branco, a padrão traduzida (como no PDF). */
    public labelOf(key: SemaphoreStatus): string {
        const label = this.value()
            .scale.find(s => s.key === key)
            ?.label.trim();
        return label || this._translate.instant(SEMAPHORE_SCALE_LABEL_KEYS[key]);
    }

    public scaleLabelRaw(key: SemaphoreStatus): string {
        return this.value().scale.find(s => s.key === key)?.label ?? '';
    }

    // #region * [Edição]

    public setStatus(index: number, key: SemaphoreStatus): void {
        if (this.blocked()) return;

        this.value.update(v => ({
            ...v,
            items: v.items.map((item, i) =>
                i === index ? { ...item, status: item.status === key ? null : key } : item
            ),
        }));
        this.commit();
    }

    public onLabelInput(index: number, text: string): void {
        this.value.update(v => ({
            ...v,
            items: v.items.map((item, i) => (i === index ? { ...item, label: text } : item)),
        }));
    }

    /** "+ Descrição": abre o campo vazio na linha e leva o foco para ele. */
    public openDescription(index: number): void {
        if (this.blocked()) return;

        this.value.update(v => ({
            ...v,
            items: v.items.map((item, i) => (i === index ? { ...item, description: '' } : item)),
        }));
        setTimeout(() =>
            this._host.nativeElement.querySelector<HTMLElement>(`[data-desc="${index}"]`)?.focus()
        );
    }

    public onDescriptionInput(index: number, text: string): void {
        this.value.update(v => ({
            ...v,
            items: v.items.map((item, i) => (i === index ? { ...item, description: text } : item)),
        }));
    }

    /** Saiu do campo vazio: a linha volta a mostrar só o botão. */
    public onDescriptionBlur(index: number): void {
        const item = this.value().items[index];
        if (item === undefined || item.description?.trim()) return;

        this.value.update(v => ({
            ...v,
            items: v.items.map((it, i) => {
                if (i !== index) return it;
                const { description: _omit, ...rest } = it;
                return rest;
            }),
        }));
    }

    public onScaleInput(key: SemaphoreStatus, event: Event): void {
        const label = (event.target as HTMLInputElement).value;
        this.value.update(v => ({
            ...v,
            scale: v.scale.map(s => (s.key === key ? { ...s, label } : s)),
        }));
    }

    /** Enter confirma o campo (o nome é uma linha só). */
    public blurOnEnter(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        (event.target as HTMLElement | null)?.blur();
    }

    public onFocusIn(): void {
        this._focused.set(true);
    }

    /** Saiu de um campo: grava se mudou. Saiu do card inteiro: libera o eco. */
    public onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;
        if (!next || !this._host.nativeElement.contains(next)) this._focused.set(false);
        this.commit();
    }

    public addItem(): void {
        if (this.blocked() || !this.canAdd()) return;

        this.value.update(v => ({ ...v, items: [...v.items, emptySemaphoreItem()] }));
        this.commit();

        const index = this.value().items.length - 1;
        setTimeout(() => this.focusLabel(index));
    }

    /** Remove na hora; o toast oferece Desfazer, que reinsere o item onde estava. */
    public removeItem(index: number): void {
        if (this.blocked()) return;
        const removed = this.value().items[index];
        if (removed === undefined) return;

        this.value.update(v => ({ ...v, items: v.items.filter((_, i) => i !== index) }));
        this.commit();

        this._undoToast.show('document.edit.semaphoreCard.itemRemoved', () => {
            if (this.blocked()) return;
            this.value.update(v => {
                const items = [...v.items];
                items.splice(Math.min(index, items.length), 0, removed);
                return { ...v, items };
            });
            this.commit();
        });
    }

    public drop(event: CdkDragDrop<unknown>): void {
        if (event.previousIndex === event.currentIndex) return;

        const items = [...this.value().items];
        moveItemInArray(items, event.previousIndex, event.currentIndex);
        this.value.update(v => ({ ...v, items }));
        this.commit();
    }

    /** Grava o valor se ele mudou em relação ao que o campo tem. */
    public commit(): void {
        const field = this.currentField();
        if (!field?._id || this.type() !== CardTypes.DEFAULT || this.blocked()) return;

        const current = this.value();
        const next: DocumentSemaphoreValue = {
            scale: current.scale.map(s => ({
                ...s,
                label: cleanSemaphoreText(s.label, SEMAPHORE_LIMITS.MAX_SCALE_LABEL_LENGTH),
            })),
            items: current.items.map(item => {
                const description = cleanSemaphoreText(
                    item.description ?? '',
                    SEMAPHORE_LIMITS.MAX_DESCRIPTION_LENGTH
                );
                return {
                    label: cleanSemaphoreText(item.label, SEMAPHORE_LIMITS.MAX_LABEL_LENGTH),
                    status: item.status,
                    // Vazio não grava; "" local só marca o campo aberto (ver openDescription).
                    ...(description
                        ? { description }
                        : item.description === ''
                          ? { description: '' }
                          : {}),
                };
            }),
        };
        if (sameSemaphoreValue(next, parseSemaphoreValue(field.value))) return;

        this.value.set(next);
        this._editItemsService.updateSemaphore(field._id, next);
    }

    public focusLabel(index: number): void {
        this._host.nativeElement.querySelector<HTMLElement>(`[data-label="${index}"]`)?.focus();
    }

    // #endregion
}
